// grid logs [<name>] [--tail N] [--follow] [--since <duration>]  — M8a.2
//
// Stream logs from the entity's pods.
//
// Resolution (spec v17 §13.2):
//   - No <name> arg, in a linked dir:  uses link.json entity_id + validates
//     active-org mismatch (§5).
//   - <name> arg given: passes the name directly as the :id path segment;
//     the server resolves UUID or display_name.
//
// Flags:
//   --tail N           Number of recent lines to fetch (default 100)
//   --follow           Stream logs continuously via SSE
//   --since <duration> Show logs since duration (e.g. 5m, 1h, 2d)
//   --deploy <id>      Backlog 271 — show the Cloud Build log excerpt
//                      captured for a specific failed deploy instead of
//                      streaming runtime logs. Reads
//                      `error.build_log_excerpt` from `GET /deploys/<id>`.

import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../api/v2-client.js';
import { V2ApiError } from '../api/v2-errors.js';
import { CloudGridError } from '../utils.js';
import { requireLink, validateLinkVsActiveOrg } from '../services/link.js';
import { tryGetActiveOrg } from '../active-org.js';
import { ServiceColorMap, renderLine, type LogLine } from '../logs-renderer.js';
import { lookupEntityByIdOrName, type EntityKindInfo } from '../lib/assert-entity-kind.js';
import { renderDeployBuildLogExcerpt } from '../services/deploy-build-log-renderer.js';
import type { DeployTrace } from '../services/plug-poll.js';
import { Output } from '../output.js';
import { addOutputOption, isStructuredOutput } from '../services/output-format.js';
import chalk from 'chalk';

/**
 * Emit a single log line. Structured mode writes one NDJSON object per line
 * (streaming JSON — a single top-level array would never close on a `--follow`
 * stream); human mode keeps the coloured `renderLine` output byte-for-byte.
 */
function emitLogLine(line: LogLine, colors: ServiceColorMap, out: Output): void {
  if (out.isStructured) {
    process.stdout.write(JSON.stringify(line) + '\n');
  } else {
    process.stdout.write(renderLine(line, colors, { verbose: false }) + '\n');
  }
}

// ── Entity resolution ─────────────────────────────────────────────────────

/**
 * Returns the entity identifier to use as the :id path segment.
 *
 * No name arg → reads link.json, validates org mismatch if active org is known,
 * and returns the link's entity_id (a server-issued UUID).
 *
 * Name arg → returns as-is; the server resolves UUID or display_name.
 */
async function resolveEntityId(
  client: IV2Client,
  nameArg: string | undefined,
): Promise<string> {
  if (nameArg !== undefined) return nameArg;
  const link = requireLink();
  const activeOrg = await tryGetActiveOrg(client);
  if (activeOrg !== null) {
    validateLinkVsActiveOrg(link, activeOrg);
  }
  return link.entity_id;
}

// ── Command implementation ────────────────────────────────────────────────

export interface LogsOpts {
  tail: number;
  follow: boolean;
  since: string | undefined;
  /**
   * Backlog 271 — trace id of a failed deploy. When set, the command
   * fetches `GET /deploys/<id>` and renders the captured Cloud Build
   * log excerpt (`error.build_log_excerpt`) plus the console URL —
   * runtime log streaming is skipped entirely. Optional so existing
   * test fixtures that pre-date this flag still typecheck.
   */
  deploy?: string;
  json?: boolean;
  output?: string;
}

/**
 * Backlog 271 — `grid logs <entity> --deploy <id>` branch.
 *
 * Fetches the deploy trace by id and renders the captured build log
 * excerpt + Cloud Build deep link. When the excerpt is absent (legacy
 * deploy event, non-build failure, or fetch error on the server side),
 * the renderer prints the §23-voiced fallback (`grid follow <id>`
 * or the console URL) on stdout — that body is the user's path
 * forward, so we exit 0 rather than throwing a redundant message.
 */
async function runDeployBuildLog(
  apiClient: IV2Client,
  deployId: string,
  out: Output,
): Promise<void> {
  let trace: DeployTrace;
  try {
    trace = await apiClient.get<DeployTrace>(`/deploys/${deployId}`);
  } catch (err) {
    if (err instanceof V2ApiError && err.code === 'NOT_FOUND') {
      throw new CloudGridError(
        `Deploy ${deployId} not found (or not in your active grid).`,
      );
    }
    throw err;
  }
  const rendered = renderDeployBuildLogExcerpt(trace);
  if (out.isStructured) {
    out.json(() => ({ deploy: deployId, log: rendered.body }));
  } else {
    process.stdout.write(rendered.body);
  }
}

/**
 * Core implementation. Accepts an optional `client` override for unit tests.
 * In production, the client is created via `getV2Client()`.
 */
export async function runLogs(
  nameArg: string | undefined,
  opts: LogsOpts,
  client?: IV2Client,
): Promise<void> {
  const apiClient = client ?? (await getV2Client());
  const out = Output.fromOpts({ json: !!opts.json, quiet: false, output: opts.output });

  // Backlog 271 — --deploy short-circuits runtime log streaming. We
  // accept (but don't require) the entity arg in this mode — the deploy
  // trace itself names the entity.
  if (opts.deploy !== undefined && opts.deploy.length > 0) {
    await runDeployBuildLog(apiClient, opts.deploy, out);
    return;
  }

  const entityId = await resolveEntityId(apiClient, nameArg);
  const colors = new ServiceColorMap();

  if (opts.follow) {
    // SSE streaming mode with single-retry-on-clean-close (backlog 113
    // audit follow-up). The upstream stream can end without a fatal SSE
    // `error` event when the TCP connection drops mid-flight (ingress
    // restart, node eviction, transient network blip). Without a retry
    // the CLI would silently return and the user would think the entity
    // had stopped logging — strictly worse than `tail -f`.
    //
    // Strategy: at most one immediate reconnect after a clean close. The
    // user gets a single stderr line announcing the retry; if the retry
    // also closes cleanly with no data, exit 0 with a final stderr line.
    // Backoff and multi-retry are deferred to a follow-up if real outages
    // show the single-retry is insufficient.
    const qs: Record<string, string | number> = { tail: opts.tail };
    if (opts.since !== undefined) qs.since = opts.since;

    const ctrl = new AbortController();
    const onSigint = () => ctrl.abort();
    process.on('SIGINT', onSigint);

    /**
     * Pump one SSE connection. Returns the number of `log` events
     * observed so the outer loop can decide whether the second close
     * was empty (no data) or just another clean drop.
     */
    const pumpOnce = async (): Promise<number> => {
      let logEventsSeen = 0;
      for await (const event of apiClient.streamSSE(
        `/api/v2/runtimes/${encodeURIComponent(entityId)}/logs/stream`,
        qs,
        ctrl.signal,
      )) {
        try {
          if (event.event === 'log') {
            logEventsSeen += 1;
            const entry = JSON.parse(event.data) as LogLine;
            emitLogLine(entry, colors, out);
          } else if (event.event === 'truncated') {
            const info = JSON.parse(event.data) as { pod: string; started: string; requested: string };
            process.stderr.write(
              chalk.dim(`[truncated: pod ${info.pod}, requested ${info.requested}]\n`),
            );
          } else if (event.event === 'error') {
            const errData = JSON.parse(event.data) as { fatal?: boolean; message: string };
            process.stderr.write(`Error: ${errData.message}\n`);
            if (errData.fatal) process.exit(1);
          }
        } catch {
          // Malformed SSE event data — skip and continue streaming.
          process.stderr.write(chalk.dim(`[skipping malformed event: ${event.event}]\n`));
        }
      }
      return logEventsSeen;
    };

    try {
      await pumpOnce();
      // Clean upstream close without ctrl-c — announce and retry once.
      if (!ctrl.signal.aborted) {
        process.stderr.write(chalk.dim('[stream closed by upstream — retrying once]\n'));
        await pumpOnce();
        if (!ctrl.signal.aborted) {
          process.stderr.write(chalk.dim('[stream closed]\n'));
        }
      }
    } finally {
      process.off('SIGINT', onSigint);
    }
    return;
  }

  // Non-streaming: fetch tail/since.
  //
  // Backlog 268 — when the API returns RUNTIME_NOT_FOUND we don't reflect
  // the K8s-shaped code at the user; we try to figure out *why* and emit
  // §23-voiced guidance: inspiration → "open it", failed runtime → "see
  // the deploy trace", neither → retry once for the transient case and
  // then surface the original error.
  const qs: Record<string, string | number> = { tail: opts.tail };
  if (opts.since !== undefined) qs.since = opts.since;
  try {
    const result = await apiClient.get<{ lines: LogLine[]; next_cursor: null }>(
      `/api/v2/runtimes/${encodeURIComponent(entityId)}/logs`,
      qs,
    );
    for (const line of result.lines) {
      emitLogLine(line, colors, out);
    }
  } catch (err) {
    if (err instanceof V2ApiError && err.code === 'RUNTIME_NOT_FOUND') {
      await handleLogsRuntimeNotFound(apiClient, entityId, qs, colors, out);
      return;
    }
    throw err;
  }
}

/**
 * Backlog 268 — render the right recovery sentence (or retry once) when
 * `/runtimes/:id/logs` returns RUNTIME_NOT_FOUND. We resolve the entity
 * once so the user gets one of three answers:
 *
 *   1. Inspiration → "Inspirations are statically served. Open it: ..."
 *   2. Runtime in a failed/stopped/archived state → "no current runtime;
 *      see the last deploy trace via `grid deploys <name>`."
 *   3. Anything else (live runtime that just lost its pods mid-flight,
 *      or no entity by that name at all) → retry once with a 1s backoff
 *      before propagating the original error.
 *
 * The lookup is bounded: at most one extra GET, then at most one retry.
 * If everything still fails the original `RUNTIME_NOT_FOUND` rises up
 * the stack so `handleV2Error` does its normal job.
 */
async function handleLogsRuntimeNotFound(
  apiClient: IV2Client,
  entityId: string,
  qs: Record<string, string | number>,
  colors: ServiceColorMap,
  out: Output,
): Promise<void> {
  // The lookup itself may fail (auth, 5xx, transient network). Don't let
  // the secondary error replace the original RUNTIME_NOT_FOUND — if we
  // can't classify the entity, fall through to the transient retry path
  // and surface the canonical error from there.
  let info: EntityKindInfo | null = null;
  try {
    info = await lookupEntityByIdOrName(apiClient, entityId);
  } catch {
    // Treat lookup failure as transient — handled below by the retry.
  }

  if (info && info.realm === 'inspiration') {
    throw new CloudGridError(
      `${info.name} is an inspiration. Inspirations are statically served and don't produce logs. ` +
        `Open it: grid open ${info.name}.`,
    );
  }

  if (info && info.realm === 'runtime') {
    const failedStates = new Set(['failed', 'stopped', 'archived', 'expired', 'charged']);
    if (info.status !== undefined && failedStates.has(info.status)) {
      throw new CloudGridError(
        `${info.name} has no current runtime (last deploy state: ${info.status}). ` +
          `To see the last deploy trace: grid deploys ${info.name}.`,
      );
    }
  }

  // Transient case — runtime exists and is supposed to be live, or the
  // entity name didn't resolve at all. Sleep 1s and try once more before
  // giving up. The retry surfaces the original V2ApiError on failure so
  // the user still sees the canonical RUNTIME_NOT_FOUND if the platform
  // really doesn't have this entity.
  await new Promise<void>((resolve) => setTimeout(resolve, 1000));
  const result = await apiClient.get<{ lines: LogLine[]; next_cursor: null }>(
    `/api/v2/runtimes/${encodeURIComponent(entityId)}/logs`,
    qs,
  );
  for (const line of result.lines) {
    emitLogLine(line, colors, out);
  }
}

// ── Commander command export ──────────────────────────────────────────────

export const logsCommand = addOutputOption(
  new Command('logs')
    .description('Stream logs from the entity pods')
    .argument('[name]', 'Entity name or UUID (uses linked entity if omitted)')
    .option('--tail <n>', 'Number of recent lines to fetch', (v) => parseInt(v, 10), 100)
    .option('--follow', 'Stream logs continuously')
    .option('--since <duration>', 'Show logs since duration (e.g. 5m, 1h, 2d)')
    .option(
      '--deploy <id>',
      'Show the captured Cloud Build log excerpt for this failed deploy id',
    )
    .option('--json', 'Machine-readable NDJSON output (alias of -o json)'),
).action(async (name: string | undefined, opts: LogsOpts) => {
  try {
    await runLogs(name, opts);
  } catch (err) {
    const { handleV2Error } = await import('../handle-error.js');
    await handleV2Error(err, { json: isStructuredOutput(opts) });
  }
});
